import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
    Box, Typography, Grid, Chip, Button, IconButton, Avatar,
    Stack, Divider, Paper, Rating, Tooltip, Tabs, Tab,
    Table, TableBody, TableCell, TableRow, TableContainer,
    Breadcrumbs, Link, Badge, LinearProgress, Skeleton,
} from "@mui/material";
import {
    FavoriteBorder, Favorite, Share, ShoppingCart,
    FlashOn, LocalShipping, VerifiedUser, Replay,
    StoreMallDirectory, Star, ExpandMore, ExpandLess,
    CheckCircle, NavigateNext, ArrowBackIos,
    ArrowForwardIos, Inventory2, Info,
    OpenInNew, ContentCopy, BrokenImage,
} from "@mui/icons-material";
import apiClient from "../utils/apiClient";
import { getImageFullUrl } from "../utils/common";

// ─── Config ───────────────────────────────────────────────────────────────────
// Change this to your actual backend base URL

const imgUrl = (path) => {
    if (!path) return "";
    // Already a full URL (http/https)
    if (path.startsWith("http")) return path;
    return getImageFullUrl(path);
};

// ─── Data Normalizer ──────────────────────────────────────────────────────────
// Converts raw API response into the shape the UI expects
function normalizeProduct(raw) {
    if (!raw) return null;

    const parseJsonField = (field, fallback) => {
        if (!field) return fallback;
        if (typeof field !== "string") return field;
        try { return JSON.parse(field); } catch { return fallback; }
    };

    const images = parseJsonField(raw.images, []).map(imgUrl);
    const thumbnail = imgUrl(raw.thumbnail);
    const specifications = parseJsonField(raw.specifications, {});
    const bannerImage = imgUrl(raw.bannerImage);

    const variants = (raw.variants || []).map((v) => ({
        ...v,
        price: Number(v.price) || 0,
        oldPrice: Number(v.oldPrice) || 0,
        stock: Number(v.stock) || 0,
        stockSell: Number(v.stockSell) || 0,
        unitQty: Number(v.unitQty) || 1,
        images: parseJsonField(v.images, []).map(imgUrl),
        thumbnail: imgUrl(v.thumbnail),
    }));

    return {
        ...raw,
        price: raw.price ? Number(raw.price) : null,
        oldPrice: raw.oldPrice ? Number(raw.oldPrice) : null,
        stock: Number(raw.stock) || 0,
        stockSell: Number(raw.stockSell) || 0,
        unitQty: Number(raw.unitQty) || 1,
        images,
        thumbnail,
        specifications,
        bannerImage,
        variants,
        // flatten category fields for easy access
        categoryName: raw.category?.name || "",
        subCategoryName: raw.subCategory?.name || "",
        thirdCategoryName: raw.thirdCategory?.name || "",
    };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const discountPct = (price, old) =>
    old && price && old > price ? Math.round(((old - price) / old) * 100) : 0;

const fmt = (n) =>
    n != null ? `₹${Number(n).toLocaleString("en-IN")}` : "—";

const stockColor = (s) => (s === 0 ? "error" : s < 5 ? "warning" : "success");
const stockLabel = (s) => (s === 0 ? "Out of Stock" : s < 5 ? `Only ${s} left` : "In Stock");

// ─── Skeleton Loader ──────────────────────────────────────────────────────────
function ProductSkeleton() {
    return (
        <Grid container spacing={4}>
            <Grid item size={{ xs: 12, md: 5 }}>
                <Skeleton variant="rounded" height={420} sx={{ borderRadius: 3, mb: 1.5 }} />
                <Stack direction="row" spacing={1}>
                    {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" width={68} height={68} sx={{ borderRadius: 2 }} />)}
                </Stack>
            </Grid>
            <Grid item size={{ xs: 12, md: 7 }}>
                <Skeleton width="60%" height={28} sx={{ mb: 1 }} />
                <Skeleton width="40%" height={22} sx={{ mb: 2 }} />
                <Skeleton width="30%" height={48} sx={{ mb: 1 }} />
                <Skeleton width="100%" height={120} sx={{ mb: 2 }} />
                <Skeleton width="100%" height={56} />
            </Grid>
        </Grid>
    );
}

// ─── Image Gallery ────────────────────────────────────────────────────────────
function ImageGallery({ images = [], thumbnail = "" }) {
    const all = images.length ? images : thumbnail ? [thumbnail] : [];
    const [active, setActive] = useState(0);
    const [zoom, setZoom] = useState(false);
    const [imgError, setImgError] = useState(false);

    // Reset when images change
    useEffect(() => { setActive(0); setImgError(false); }, [images, thumbnail]);

    const prev = () => { setActive((a) => (a - 1 + all.length) % all.length); setImgError(false); };
    const next = () => { setActive((a) => (a + 1) % all.length); setImgError(false); };

    if (!all.length) {
        return (
            <Box sx={{ aspectRatio: "1/1", borderRadius: 3, bgcolor: "grey.100", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid", borderColor: "divider" }}>
                <Stack alignItems="center" spacing={1}>
                    <BrokenImage sx={{ fontSize: 56, color: "grey.300" }} />
                    <Typography fontSize={13} color="text.disabled">No image available</Typography>
                </Stack>
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ position: "relative", borderRadius: 3, overflow: "hidden", mb: 1.5, bgcolor: "grey.50", border: "1px solid", borderColor: "divider" }}>
                {imgError ? (
                    <Box sx={{ aspectRatio: "1/1", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <BrokenImage sx={{ fontSize: 56, color: "grey.300" }} />
                    </Box>
                ) : (
                    <Box
                        component="img"
                        src={all[active]}
                        alt="product"
                        onError={() => setImgError(true)}
                        onMouseEnter={() => setZoom(true)}
                        onMouseLeave={() => setZoom(false)}
                        sx={{
                            width: "100%", aspectRatio: "1/1", objectFit: "cover", display: "block",
                            transition: "transform 0.4s ease",
                            transform: zoom ? "scale(1.08)" : "scale(1)",
                            cursor: "zoom-in",
                        }}
                    />
                )}

                {all.length > 1 && (
                    <>
                        <IconButton onClick={prev} size="small" sx={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", bgcolor: "rgba(255,255,255,0.9)", "&:hover": { bgcolor: "#fff" }, boxShadow: 2 }}>
                            <ArrowBackIos fontSize="small" sx={{ fontSize: 14, ml: 0.5 }} />
                        </IconButton>
                        <IconButton onClick={next} size="small" sx={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", bgcolor: "rgba(255,255,255,0.9)", "&:hover": { bgcolor: "#fff" }, boxShadow: 2 }}>
                            <ArrowForwardIos fontSize="small" sx={{ fontSize: 14 }} />
                        </IconButton>
                    </>
                )}
                {all.length > 1 && (
                    <Box sx={{ position: "absolute", bottom: 10, right: 10, bgcolor: "rgba(0,0,0,0.45)", color: "#fff", fontSize: 11, px: 1, py: 0.3, borderRadius: 1 }}>
                        {active + 1} / {all.length}
                    </Box>
                )}
            </Box>

            {all.length > 1 && (
                <Stack direction="row" spacing={1} sx={{ overflowX: "auto", pb: 0.5 }}>
                    {all.map((img, i) => (
                        <Box key={i} onClick={() => { setActive(i); setImgError(false); }}
                            sx={{
                                width: 68, height: 68, flexShrink: 0, borderRadius: 2, overflow: "hidden", cursor: "pointer",
                                border: "2px solid", borderColor: i === active ? "primary.main" : "divider",
                                transition: "border-color 0.2s", opacity: i === active ? 1 : 0.65,
                                "&:hover": { opacity: 1 },
                            }}>
                            <img src={img} alt={`thumb-${i}`} style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                onError={(e) => { e.target.style.display = "none"; }} />
                        </Box>
                    ))}
                </Stack>
            )}
        </Box>
    );
}

// ─── Variant Selector ─────────────────────────────────────────────────────────
function VariantSelector({ product, onSelect }) {
    const [selections, setSelections] = useState({});

    const isValueAvailable = (attrName, val) =>
        product.variants.some(
            (v) =>
                v.status === "active" &&
                v.stock > 0 &&
                v.attributes.find((a) => a.attributeName === attrName && a.attributeValue === val)
        );

    const toggleAttr = (name, val) => {
        const next = { ...selections, [name]: selections[name] === val ? undefined : val };
        setSelections(next);

        const activeKeys = Object.keys(next).filter((k) => next[k]);
        if (!activeKeys.length) { onSelect(null); return; }

        const match = product.variants.find((v) =>
            activeKeys.every((k) =>
                v.attributes.find((a) => a.attributeName === k && a.attributeValue === next[k])
            )
        );
        onSelect(match || null);
    };

    // Derive color value from CSS-named colors or known map
    const colorMap = {
        ivory: "#FFFFF0", navy: "#1B2A4A", sage: "#8FAF8F",
        red: "#e53935", blue: "#1565C0", green: "#2E7D32",
        black: "#212121", white: "#FAFAFA", yellow: "#F9A825",
        pink: "#E91E63", purple: "#7B1FA2", orange: "#E65100",
        grey: "#9E9E9E", gray: "#9E9E9E", brown: "#5D4037",
    };
    const getColorHex = (val) => colorMap[val.toLowerCase()] || null;

    return (
        <Box>
            {product.attributes.map((attr) => {
                const isColor = attr.name.toLowerCase() === "color";
                return (
                    <Box key={attr.id || attr.name} mb={2}>
                        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                            <Typography fontSize={13} fontWeight={700} color="text.secondary"
                                sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                                {attr.name}
                            </Typography>
                            {selections[attr.name] && (
                                <Typography fontSize={13} fontWeight={600} color="primary.main">
                                    : {selections[attr.name]}
                                </Typography>
                            )}
                        </Stack>
                        <Stack direction="row" flexWrap="wrap" gap={1}>
                            {attr.values.map((v) => {
                                const val = v.value;
                                const available = isValueAvailable(attr.name, val);
                                const selected = selections[attr.name] === val;
                                const colorHex = isColor ? getColorHex(val) : null;

                                return (
                                    <Tooltip key={v.id || val} title={available ? val : `${val} — unavailable`}>
                                        <Box
                                            onClick={() => available && toggleAttr(attr.name, val)}
                                            sx={{
                                                px: isColor ? 0 : 1.5,
                                                py: isColor ? 0 : 0.6,
                                                width: isColor ? 34 : "auto",
                                                height: isColor ? 34 : "auto",
                                                borderRadius: isColor ? "50%" : 1.5,
                                                border: "2px solid",
                                                borderColor: selected ? "primary.main" : available ? "divider" : "grey.200",
                                                cursor: available ? "pointer" : "not-allowed",
                                                opacity: available ? 1 : 0.4,
                                                transition: "all 0.15s",
                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                bgcolor: isColor ? (colorHex || "grey.300") : selected ? "primary.50" : "white",
                                                "&:hover": available ? { borderColor: "primary.light", bgcolor: isColor ? undefined : "primary.50" } : {},
                                                position: "relative",
                                            }}
                                        >
                                            {isColor ? (
                                                selected && <CheckCircle sx={{ fontSize: 14, color: colorHex && colorHex < "#888888" ? "#fff" : "#333" }} />
                                            ) : (
                                                <Typography fontSize={13} fontWeight={selected ? 700 : 500}
                                                    color={selected ? "primary.main" : available ? "text.primary" : "text.disabled"}>
                                                    {val}
                                                </Typography>
                                            )}
                                            {/* Strike-through for unavailable non-color chips */}
                                            {!available && !isColor && (
                                                <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                    <Box sx={{ position: "absolute", width: "100%", height: "1px", bgcolor: "grey.400", transform: "rotate(-35deg)" }} />
                                                </Box>
                                            )}
                                        </Box>
                                    </Tooltip>
                                );
                            })}
                        </Stack>
                    </Box>
                );
            })}
        </Box>
    );
}

// ─── Rating Bar ───────────────────────────────────────────────────────────────
function RatingBar({ label, value, count }) {
    return (
        <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography fontSize={12} sx={{ width: 16, textAlign: "right", color: "text.secondary" }}>{label}</Typography>
            <Star sx={{ fontSize: 13, color: "#f5a623" }} />
            <LinearProgress variant="determinate" value={value}
                sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: "grey.100", "& .MuiLinearProgress-bar": { bgcolor: "#f5a623" } }} />
            <Typography fontSize={12} color="text.secondary" sx={{ width: 28 }}>{count}</Typography>
        </Stack>
    );
}

function TabPanel({ value, index, children }) {
    return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
export default function ProductDetailsPage() {
    const { productId } = useParams();

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [wishlist, setWishlist] = useState(false);
    const [qty, setQty] = useState(1);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [tab, setTab] = useState(0);
    const [showAllSpecs, setShowAllSpecs] = useState(false);
    const [copied, setCopied] = useState(false);

    // ── Fetch ──────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!productId) return;
        setLoading(true);
        setError("");
        apiClient
            .get(`/api/new-products/${productId}`)
            .then((res) => {
                const normalized = normalizeProduct(res?.data?.data);
                setProduct(normalized);
            })
            .catch((err) => {
                setError(err?.response?.data?.message || "Failed to load product.");
            })
            .finally(() => setLoading(false));
    }, [productId]);

    // ── Derived values ─────────────────────────────────────────────────────────
    const activePrice = selectedVariant ? Number(selectedVariant.price) : product?.price;
    const activeOldPrice = selectedVariant ? Number(selectedVariant.oldPrice) : product?.oldPrice;
    const activeStock = selectedVariant ? selectedVariant.stock : (product?.stock ?? 0);
    const activeSku = selectedVariant?.sku ?? "";
    const activeSoldPct = product
        ? Math.min(100, Math.round(((product.stockSell) / (product.stockSell + activeStock || 1)) * 100))
        : 0;

    const specsEntries = Object.entries(product?.specifications || {});
    const visibleSpecs = showAllSpecs ? specsEntries : specsEntries.slice(0, 6);

    const approvalColor = { pending: "warning", approved: "success", rejected: "error" };

    const copyCode = () => {
        if (!activeSku) return;
        navigator.clipboard.writeText(activeSku);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    // ── States ─────────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <Box sx={{ maxWidth: 1100, mx: "auto", p: { xs: 2, md: 3 } }}>
                <Skeleton width={320} height={22} sx={{ mb: 3 }} />
                <ProductSkeleton />
            </Box>
        );
    }

    if (error || !product) {
        return (
            <Box sx={{ maxWidth: 1100, mx: "auto", p: { xs: 2, md: 3 }, textAlign: "center", pt: 10 }}>
                <BrokenImage sx={{ fontSize: 64, color: "grey.300", mb: 2 }} />
                <Typography variant="h6" fontWeight={700} mb={1}>Product Not Found</Typography>
                <Typography color="text.secondary">{error || "This product does not exist or has been removed."}</Typography>
            </Box>
        );
    }

    const disc = discountPct(activePrice, activeOldPrice);

    return (
        <Paper>
            <Box sx={{ maxWidth: 1100, mx: "auto", p: { xs: 2, md: 3 } }} >

                {/* ── Gallery + Info ───────────────────────────────────────────────── */}
                <Grid container spacing={4}>

                    {/* Gallery */}
                    <Grid item size={{ xs: 12, md: 5 }}>
                        {/* Show variant images if a variant with images is selected, else product images */}
                        <ImageGallery
                            images={
                                selectedVariant?.images?.length
                                    ? selectedVariant.images
                                    : product.images
                            }
                            thumbnail={
                                selectedVariant?.thumbnail || product.thumbnail
                            }
                        />
                        {/* <Stack direction="row" spacing={1} mt={2}>
                        <Button fullWidth variant="outlined" size="small"
                            startIcon={wishlist ? <Favorite sx={{ color: "error.main" }} /> : <FavoriteBorder />}
                            onClick={() => setWishlist((w) => !w)}
                            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}>
                            {wishlist ? "Wishlisted" : "Add to Wishlist"}
                        </Button>
                        <Tooltip title="Share product">
                            <IconButton size="small" sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}
                                onClick={() => navigator.share?.({ title: product.name, url: window.location.href })}>
                                <Share fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack> */}
                    </Grid>

                    {/* Info */}
                    <Grid item size={{ xs: 12, md: 7 }}>

                        {/* Status badges */}
                        <Stack direction="row" flexWrap="wrap" gap={0.8} mb={1.5}>
                            {product.isFeatured && <Chip label="Featured" size="small" color="primary" sx={{ fontWeight: 700, fontSize: 11 }} />}
                            {product.isMostSelling && <Chip label="Best Seller" size="small" sx={{ bgcolor: "#FF6F00", color: "#fff", fontWeight: 700, fontSize: 11 }} />}
                            {/* {product.showOnHomepage && <Chip label="Home Page Show" size="small" color="info" sx={{ fontWeight: 700, fontSize: 11 }} />} */}
                            <Chip
                                label={'Admin ' + product.approvalStatus}
                                size="small"
                                color={approvalColor[product.approvalStatus] || "default"}
                                sx={{ fontWeight: 700, fontSize: 11, textTransform: "capitalize" }}
                            />
                            <Chip
                                label={product.status === "active" ? "Active" : "Inactive"}
                                size="small"
                                color={product.status === "active" ? "success" : "default"}
                                sx={{ fontWeight: 700, fontSize: 11 }}
                            />
                        </Stack>

                        {/* Name */}
                        <Typography variant="h5" fontWeight={800} lineHeight={1.25} mb={0.5}>
                            {product.name}
                        </Typography>

                        {/* Brand + SKU */}
                        <Stack direction="row" alignItems="center" spacing={2} mb={1.5} flexWrap="wrap">
                            {product.brand && (
                                <>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <StoreMallDirectory fontSize="small" color="action" />
                                        <Typography fontSize={13} color="text.secondary" fontWeight={600}>{product.brand}</Typography>
                                    </Stack>
                                    <Divider orientation="vertical" flexItem />
                                </>
                            )}
                            {activeSku && 
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <Typography fontSize={12} color="text.disabled">SKU:</Typography>
                                <Typography fontSize={12} fontFamily="monospace" fontWeight={600} color="text.secondary">
                                    {activeSku || "—"}
                                </Typography>
                                {activeSku && (
                                    <Tooltip title={copied ? "Copied!" : "Copy SKU"}>
                                        <IconButton size="small" onClick={copyCode} sx={{ p: 0.3 }}>
                                            <ContentCopy sx={{ fontSize: 13 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Stack>}
                        </Stack>

                        {/* Rating (static until real reviews API) */}
                        {product.rating != null && (
                            <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                                <Rating value={Number(product.rating)} precision={0.1} readOnly size="small" />
                                <Typography fontSize={13} fontWeight={700}>{product.rating}</Typography>
                                <Typography fontSize={13} color="text.secondary">({product.reviewCount ?? 0} reviews)</Typography>
                            </Stack>
                        )}

                        <Divider sx={{ mb: 2 }} />

                        {/* Pricing */}
                        {product.isProductVariants && !selectedVariant ? (
                            <Box mb={2}>
                                <Typography fontSize={14} color="text.secondary" fontStyle="italic">
                                    Select a variant to see pricing
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <Stack direction="row" alignItems="baseline" spacing={1.5} mb={0.5} flexWrap="wrap">
                                    <Typography variant="h4" fontWeight={900}>
                                        {fmt(activePrice)}
                                    </Typography>
                                    {activeOldPrice && activeOldPrice > activePrice && (
                                        <>
                                            <Typography fontSize={18} color="text.disabled" sx={{ textDecoration: "line-through" }}>
                                                {fmt(activeOldPrice)}
                                            </Typography>
                                            {disc > 0 && (
                                                <Chip label={`${disc}% OFF`} size="small"
                                                    sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: 800, fontSize: 12 }} />
                                            )}
                                        </>
                                    )}
                                </Stack>
                                {/* <Typography fontSize={12} color="text.disabled" mb={2}>Inclusive of all taxes</Typography> */}
                            </>
                        )}

                        {/* Stock meter */}
                        <Box mb={2.5}>
                            <Stack direction="row" justifyContent="space-between" mb={0.5}>
                                <Chip size="small" label={stockLabel(activeStock)} color={stockColor(activeStock)}
                                    sx={{ fontWeight: 700, fontSize: 11 }} />
                                {activeStock > 0 && product.stockSell > 0 && (
                                    <Typography fontSize={12} color="text.secondary">
                                        {product.stockSell} sold of {product.stockSell + activeStock}
                                    </Typography>
                                )}
                            </Stack>
                            <LinearProgress variant="determinate" value={activeSoldPct}
                                sx={{
                                    height: 5, borderRadius: 3, bgcolor: "grey.100",
                                    "& .MuiLinearProgress-bar": {
                                        bgcolor: activeStock === 0 ? "error.main" : activeStock < 5 ? "warning.main" : "success.main",
                                    },
                                }}
                            />
                        </Box>

                        {/* Variant selector */}
                        {product.isProductVariants && product.attributes?.length > 0 && (
                            <Box mb={2.5}>
                                <VariantSelector product={product} onSelect={setSelectedVariant} />
                                {selectedVariant && (
                                    <Paper variant="outlined" sx={{ mt: 1.5, px: 2, py: 1.5, borderRadius: 2, bgcolor: "grey.50" }}>
                                        <Stack direction="row" flexWrap="wrap" gap={2.5}>
                                            <Box>
                                                <Typography fontSize={11} color="text.disabled">SKU</Typography>
                                                <Typography fontSize={13} fontFamily="monospace" fontWeight={700}>{selectedVariant.sku}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography fontSize={11} color="text.disabled">Stock</Typography>
                                                <Typography fontSize={13} fontWeight={700}>{selectedVariant.stock}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography fontSize={11} color="text.disabled">Price</Typography>
                                                <Typography fontSize={13} fontWeight={700}>{fmt(selectedVariant.price)}</Typography>
                                            </Box>
                                            {selectedVariant.oldPrice > 0 && (
                                                <Box>
                                                    <Typography fontSize={11} color="text.disabled">MRP</Typography>
                                                    <Typography fontSize={13} fontWeight={700} color="text.secondary"
                                                        sx={{ textDecoration: "line-through" }}>{fmt(selectedVariant.oldPrice)}</Typography>
                                                </Box>
                                            )}
                                            <Box>
                                                <Typography fontSize={11} color="text.disabled">Status</Typography>
                                                <Chip size="small" label={selectedVariant.status}
                                                    color={selectedVariant.status === "active" ? "success" : "default"}
                                                    sx={{ fontSize: 10, height: 18 }} />
                                            </Box>
                                        </Stack>
                                    </Paper>
                                )}
                            </Box>
                        )}

                        {/* Qty + CTA */}
                        {/* <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} mb={2.5}>
                        <Paper variant="outlined" sx={{ display: "flex", alignItems: "center", borderRadius: 2, overflow: "hidden", alignSelf: "flex-start" }}>
                            <IconButton size="small" onClick={() => setQty((q) => Math.max(1, q - 1))} sx={{ borderRadius: 0, px: 1.5 }}>
                                <Typography fontWeight={700} fontSize={18} lineHeight={1}>−</Typography>
                            </IconButton>
                            <Typography fontWeight={700} sx={{ px: 2, minWidth: 32, textAlign: "center" }}>{qty}</Typography>
                            <IconButton size="small" onClick={() => setQty((q) => Math.min(activeStock || 1, q + 1))}
                                sx={{ borderRadius: 0, px: 1.5 }} disabled={activeStock === 0}>
                                <Typography fontWeight={700} fontSize={18} lineHeight={1}>+</Typography>
                            </IconButton>
                        </Paper>
                        <Button fullWidth variant="outlined" size="large" startIcon={<ShoppingCart />}
                            disabled={activeStock === 0 || (product.isProductVariants && !selectedVariant)}
                            sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none", fontSize: 15 }}>
                            Add to Cart
                        </Button>
                        <Button fullWidth variant="contained" size="large" startIcon={<FlashOn />}
                            disabled={activeStock === 0 || (product.isProductVariants && !selectedVariant)}
                            sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none", fontSize: 15, bgcolor: "#FF6F00", "&:hover": { bgcolor: "#E65100" } }}>
                            Buy Now
                        </Button>
                    </Stack> */}

                        {/* Delivery info */}
                        {/* <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: "hidden" }}>
                        {[
                            { icon: <LocalShipping fontSize="small" color="primary" />, label: "Free Delivery", sub: "On orders above ₹499" },
                            { icon: <VerifiedUser fontSize="small" color="success" />, label: "100% Authentic", sub: "Verified seller products" },
                            { icon: <Replay fontSize="small" sx={{ color: "warning.main" }} />, label: "7-Day Returns", sub: "Easy returns & exchanges" },
                        ].map((item, i) => (
                            <Box key={i}>
                                {i > 0 && <Divider />}
                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2, py: 1.5 }}>
                                    {item.icon}
                                    <Box>
                                        <Typography fontSize={13} fontWeight={700}>{item.label}</Typography>
                                        <Typography fontSize={12} color="text.secondary">{item.sub}</Typography>
                                    </Box>
                                </Stack>
                            </Box>
                        ))}
                    </Paper> */}
                    </Grid>
                </Grid>

                {/* ── Detail Tabs ──────────────────────────────────────────────────── */}
                <Paper variant="outlined" sx={{ mt: 4, borderRadius: 2.5, overflow: "hidden" }}>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
                        sx={{ borderBottom: "1px solid", borderColor: "divider", bgcolor: "grey.50" }}>
                        <Tab label="Description" />
                        <Tab label="Specifications" />
                        <Tab label={
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <span>Variants</span>
                                {product.variants?.length > 0 && (
                                    <Badge badgeContent={product.variants.length} color="primary" sx={{ ml: 1 }} />
                                )}
                            </Stack>
                        } />
                        <Tab label="Reviews" />
                        <Tab label="Admin Notes" />
                    </Tabs>

                    {/* Tab 0 — Description */}
                    <TabPanel value={tab} index={0}>
                        <Box sx={{ px: 3, pb: 3 }}>
                            <Typography fontSize={15} color="text.secondary" lineHeight={1.85} mb={3}>
                                {product.description || "No description provided."}
                            </Typography>
                            <Grid container spacing={2}>
                                {[
                                    // ["Product Code", product.productCode],
                                    ["HSN Code", product.hsnCode],
                                    ["GST Percent", product.gstPercent],
                                    ["IGST Percent", product.igstPercent],
                                    ["Category", [product.categoryName, product.subCategoryName, product.thirdCategoryName].filter(Boolean).join(" › ") || "—"],
                                    ["Brand", product?.brands?.name || "—"],
                                    ["Unit", `${product.unitQty} ${product.unit}`],
                                    // ["Weight", product.weight ? `${product.weight} kg` : "—"],
                                    ["Status", product.status],
                                ].map(([k, v]) => (
                                    <Grid item size={{ xs: 6, md: 4 }} key={k}>
                                        <Box sx={{ p: 1.5, bgcolor: "grey.50", borderRadius: 2 }}>
                                            <Typography fontSize={11} color="text.disabled" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>{k}</Typography>
                                            <Typography fontSize={14} fontWeight={600} mt={0.3}>{v}</Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>

                            {/* Banner section */}
                            {product.isBanner && product.bannerImage && (
                                <Box mt={3}>
                                    <Typography fontSize={13} fontWeight={700} color="text.secondary" mb={1} sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                                        Banner
                                    </Typography>
                                    <Box sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid", borderColor: "divider", maxHeight: 200 }}>
                                        <img src={product.bannerImage} alt="banner"
                                            style={{ width: "100%", maxHeight: 200, objectFit: "cover", display: "block" }}
                                            onError={(e) => { e.target.style.display = "none"; }} />
                                    </Box>
                                    {product.bannerTitle && (
                                        <Typography fontSize={14} fontWeight={600} mt={1}>{product.bannerTitle}</Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </TabPanel>

                    {/* Tab 1 — Specifications */}
                    <TabPanel value={tab} index={1}>
                        <Box sx={{ px: 3, pb: 3 }}>
                            {specsEntries.length === 0 ? (
                                <Box textAlign="center" py={5}>
                                    <Info sx={{ fontSize: 40, color: "grey.300", mb: 1 }} />
                                    <Typography color="text.secondary">No specifications available.</Typography>
                                </Box>
                            ) : (
                                <>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableBody>
                                                {visibleSpecs.map(([key, val], i) => (
                                                    <TableRow key={key} sx={{ bgcolor: i % 2 ? "grey.50" : "white" }}>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: 13, width: "35%", color: "text.secondary", borderColor: "divider" }}>
                                                            {key}
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: 14, borderColor: "divider" }}>{val}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    {specsEntries.length > 6 && (
                                        <Button size="small" onClick={() => setShowAllSpecs((s) => !s)}
                                            endIcon={showAllSpecs ? <ExpandLess /> : <ExpandMore />}
                                            sx={{ mt: 1.5, textTransform: "none", fontWeight: 600 }}>
                                            {showAllSpecs ? "Show Less" : `Show ${specsEntries.length - 6} More`}
                                        </Button>
                                    )}
                                </>
                            )}
                        </Box>
                    </TabPanel>

                    {/* Tab 2 — Variants */}
                    <TabPanel value={tab} index={2}>
                        <Box sx={{ px: 3, pb: 3 }}>
                            {!product.isProductVariants || !product.variants?.length ? (
                                <Box textAlign="center" py={5}>
                                    <Inventory2 sx={{ fontSize: 40, color: "grey.300", mb: 1 }} />
                                    <Typography color="text.secondary">This is a simple product with no variants.</Typography>
                                </Box>
                            ) : (
                                <>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableBody>
                                                {/* Header row */}
                                                <TableRow sx={{ "& td": { fontWeight: 700, fontSize: 12, color: "text.secondary", bgcolor: "grey.50", borderColor: "divider" } }}>
                                                    <TableCell>Combination</TableCell>
                                                    <TableCell>SKU</TableCell>
                                                    <TableCell>Price</TableCell>
                                                    <TableCell>MRP</TableCell>
                                                    <TableCell>Stock</TableCell>
                                                    <TableCell>Image</TableCell>
                                                    <TableCell>Status</TableCell>
                                                </TableRow>
                                                {product.variants.map((v, i) => (
                                                    <TableRow key={v.id}
                                                        sx={{ bgcolor: i % 2 ? "grey.50" : "white", "&:hover": { bgcolor: "primary.50" } }}>
                                                        <TableCell>
                                                            <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                                                {v.attributes.map((a) => (
                                                                    <Chip key={a.id || a.attributeName} size="small"
                                                                        label={`${a.attributeName}: ${a.attributeValue}`}
                                                                        sx={{ fontSize: 10, height: 18 }} />
                                                                ))}
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography fontSize={12} fontFamily="monospace">{v.sku}</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography fontSize={13} fontWeight={700}>{fmt(v.price)}</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography fontSize={12} color="text.disabled"
                                                                sx={{ textDecoration: "line-through" }}>
                                                                {v.oldPrice ? fmt(v.oldPrice) : "—"}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip size="small" label={v.stock ?? 0}
                                                                color={stockColor(v.stock ?? 0)} sx={{ fontWeight: 700, fontSize: 11 }} />
                                                        </TableCell>
                                                        <TableCell>
                                                            {v.thumbnail ? (
                                                                <Avatar src={v.thumbnail} variant="rounded"
                                                                    sx={{ width: 32, height: 32 }}
                                                                    onError={(e) => { e.target.src = ""; }} />
                                                            ) : (
                                                                <Avatar variant="rounded" sx={{ width: 32, height: 32, bgcolor: "grey.100" }}>
                                                                    <BrokenImage sx={{ fontSize: 16, color: "grey.400" }} />
                                                                </Avatar>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip size="small" label={v.status}
                                                                color={v.status === "active" ? "success" : "default"} sx={{ fontSize: 11 }} />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <Typography fontSize={12} color="text.disabled" mt={1.5}>
                                        {product.variants.length} total ·{" "}
                                        {product.variants.filter((v) => v.stock > 0 && v.status === "active").length} available
                                    </Typography>
                                </>
                            )}
                        </Box>
                    </TabPanel>

                    {/* Tab 3 — Reviews */}
                    <TabPanel value={tab} index={3}>
                        <Box sx={{ px: 3, pb: 3 }}>
                            {!product.rating ? (
                                <Box textAlign="center" py={5}>
                                    <Star sx={{ fontSize: 40, color: "grey.300", mb: 1 }} />
                                    <Typography color="text.secondary">No reviews yet for this product.</Typography>
                                </Box>
                            ) : (
                                <Grid container spacing={4}>
                                    <Grid item size={{ xs: 12, md: 4 }}>
                                        <Box textAlign="center" mb={2}>
                                            <Typography variant="h2" fontWeight={900} lineHeight={1}>{product.rating}</Typography>
                                            <Rating value={Number(product.rating)} precision={0.1} readOnly sx={{ mt: 0.5 }} />
                                            <Typography fontSize={13} color="text.secondary" mt={0.5}>
                                                {product.reviewCount ?? 0} verified ratings
                                            </Typography>
                                        </Box>
                                        <Stack spacing={0.8}>
                                            {[5, 4, 3, 2, 1].map((s) => {
                                                const pct = s === 5 ? 62 : s === 4 ? 21 : s === 3 ? 10 : s === 2 ? 4 : 3;
                                                return <RatingBar key={s} label={s} value={pct} count={Math.round((product.reviewCount ?? 0) * pct / 100)} />;
                                            })}
                                        </Stack>
                                    </Grid>
                                    <Grid item size={{ xs: 12, md: 8 }}>
                                        <Box textAlign="center" py={5}>
                                            <Typography color="text.secondary" fontSize={14}>Connect your reviews API to display reviews here.</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            )}
                        </Box>
                    </TabPanel>

                    {/* Tab 4 — Seller Info */}
                    <TabPanel value={tab} index={4}>
                        <Box sx={{ px: 3, pb: 3 }}>
                            <Grid container spacing={3}>
                                {/* <Grid item xs={12} md={5}>
                                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2.5 }}>
                                    <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
                                        <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48, fontWeight: 800, fontSize: 20 }}>
                                            {(product.brand || product.name || "S")[0].toUpperCase()}
                                        </Avatar>
                                        <Box>
                                            <Typography fontWeight={800} fontSize={15}>{product.brand || "Seller"}</Typography>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <CheckCircle sx={{ fontSize: 13, color: "success.main" }} />
                                                <Typography fontSize={12} color="success.main" fontWeight={600}>Verified Seller</Typography>
                                            </Stack>
                                        </Box>
                                    </Stack>
                                    <Divider sx={{ mb: 2 }} />
                                    <Stack spacing={1.2}>
                                        {[
                                            ["Dispatch Time", "1–2 business days"],
                                            ["Return Policy", "7-day easy returns"],
                                        ].map(([k, v]) => (
                                            <Stack key={k} direction="row" justifyContent="space-between">
                                                <Typography fontSize={13} color="text.secondary">{k}</Typography>
                                                <Typography fontSize={13} fontWeight={700}>{v}</Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                    <Button fullWidth variant="outlined" startIcon={<OpenInNew fontSize="small" />}
                                        sx={{ mt: 2, borderRadius: 2, textTransform: "none", fontWeight: 600 }}>
                                        Visit Store
                                    </Button>
                                </Paper>
                            </Grid> */}

                                <Grid item size={{ xs: 12, md: 12 }}>
                                    <Box sx={{ p: 2.5, bgcolor: "grey.50", borderRadius: 2.5, border: "1px solid", borderColor: "divider" }}>
                                        <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                                            <Info fontSize="small" color="action" />
                                            <Typography fontWeight={700} fontSize={14}>Admin Notes</Typography>
                                            <Chip size="small" label={product.approvalStatus}
                                                color={approvalColor[product.approvalStatus] || "default"}
                                                sx={{ fontWeight: 700, fontSize: 11, textTransform: "capitalize" }} />
                                        </Stack>
                                        <Typography fontSize={14} color="text.secondary" lineHeight={1.7}>
                                            {product.adminComment || "No admin comment available."}
                                        </Typography>
                                        <Divider sx={{ my: 2 }} />
                                        <Stack spacing={0.8}>
                                            {[
                                                ["Product ID", product.id],
                                                ["Vendor ID", product.vendorId],
                                                ["Listed On", product.createdAt
                                                    ? new Date(product.createdAt).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })
                                                    : "—"],
                                                ["Approved On", product.approvedAt
                                                    ? new Date(product.approvedAt).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })
                                                    : "—"],
                                                // ["Slug", `/products/${product.slug}`],
                                            ].map(([k, v]) => (
                                                <Stack key={k} direction="row" spacing={2} alignItems="flex-start">
                                                    <Typography fontSize={12} color="text.disabled" sx={{ minWidth: 80, flexShrink: 0 }}>{k}</Typography>
                                                    <Typography fontSize={12} fontFamily="monospace" color="text.secondary"
                                                        sx={{ wordBreak: "break-all" }}>{v}</Typography>
                                                </Stack>
                                            ))}
                                        </Stack>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </TabPanel>
                </Paper>
            </Box>
        </Paper>
    );
}